import { colors } from '@beelday/common'
import { keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import { FC } from 'react'
import { LobbySize, SCALE } from './lobby-size'

const pulse = keyframes`
	0% { box-shadow: 0 0 0 0 ${colors.greenishCyan03}; }
	70% { box-shadow: 0 0 0 10px transparent; }
	100% { box-shadow: 0 0 0 0 transparent; }
`

const Line = styled.div<{ $size: LobbySize }>`
	box-sizing: border-box;
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 12px 16px;
	border-radius: 12px;
	background: ${colors.greenishCyan01};
	color: ${colors.blackish};
	font-size: ${props => SCALE[props.$size].body}px;
	font-weight: 500;
	line-height: 1.4;
`

const Dot = styled.span<{ $size: LobbySize }>`
	flex: none;
	width: ${props => (props.$size === 'room' ? 16 : 10)}px;
	height: ${props => (props.$size === 'room' ? 16 : 10)}px;
	border-radius: 50%;
	background: ${colors.greenishCyan};
	animation: ${pulse} 2s ease-out infinite;

	@media (prefers-reduced-motion: reduce) {
		animation: none;
		box-shadow: 0 0 0 4px ${colors.greenishCyan02};
	}
`

type Props = {
	size: LobbySize
}

/**
 * What the room is waiting for: a softly pulsing dot and one line. A polite
 * live region, so a screen reader hears it change; the pulse stops for anyone
 * who asked for less motion.
 */
export const LobbyStatus: FC<Props> = ({ size, children }) => (
	<Line
		$size={size}
		role="status"
		aria-live="polite"
		data-testid="in_person.lobby.status"
	>
		<Dot $size={size} aria-hidden="true" />
		<span>{children}</span>
	</Line>
)
